/**
 * NormalEdges.ts
 * -----------------------------------------------------------------------------
 * INTERSECTION LINES: the line where two solids interpenetrate.
 *
 * This is the third kind of line, and the only one of the three that cannot be
 * drawn as geometry. The other two have something to hang a mesh on:
 *
 *   SelectionHull    the SILHOUETTE — where a component ends. Real geometry at
 *                    the component's own depth.
 *   OutlineCreases   the CREASES — where two faces of one component meet along a
 *                    shared edge. `EdgesGeometry` finds it in the triangles.
 *
 * An INTERSECTION has neither. When a bearing block is modelled with its base
 * sunk into the pier cap, the curve where its side faces cross the cap's top
 * face is not an edge of either mesh — it runs through the middle of the cap's
 * face, and the block's own bottom rim is buried out of sight below it. There is
 * no vertex there, no edge, and no topology of any kind. `EdgesGeometry` cannot
 * find it (and it is not the threshold: three's implementation also emits every
 * unmatched boundary edge, so an open shell's rim is always drawn — the reason
 * nothing appears here is that there is genuinely nothing to emit). Getting it
 * as geometry would mean solving surface-surface intersection between every pair
 * of components, i.e. CSG, which is expensive, fragile on the exporter's
 * triangle soup, and would have to be redone whenever the model changes.
 *
 * WHAT IS THERE INSTEAD IS A NORMAL DISCONTINUITY. Across that curve the shaded
 * image jumps from "facing up" (the cap's top) to "facing sideways" (the block's
 * flank) in one pixel, even though DEPTH is continuous — the surfaces touch. So
 * the line is invisible to a depth-based edge detector and obvious to a
 * normal-based one, and screen space is the natural place to look for it,
 * because on screen the curve exists whether or not the geometry knows about it.
 *
 * WHY THIS DOES NOT BRING BACK THE `OutlinePass` PROBLEMS. The objection to a
 * post-process was never that post-processing is wrong — it was that routing the
 * MAIN image through an `EffectComposer` costs the renderer's MSAA (a composer's
 * buffer does not inherit `antialias`) and its tone mapping (three skips tone
 * mapping when it is not drawing to the screen). Nothing here touches the main
 * image. The frame is still drawn by the viewport's own
 * `renderer.render(scene, camera)` straight to the canvas with MSAA and
 * ACESFilmic intact; this adds a normal-only prepass into an offscreen target
 * and then draws ONE transparent full-screen quad over the finished frame. No
 * composer, no `OutputPass`, no second copy of the shaded image.
 *
 * WHAT IT COSTS is one extra pass over the surface geometry per drawn frame,
 * into a buffer at twice the canvas resolution (SS_SCALE — the line's own 4x
 * antialiasing), plus a full-screen quad. Viewport3D renders on demand, so an idle viewport
 * pays nothing. The prepass is restricted by LAYER to the bridge surfaces —
 * without that, the crease lines, the hull, the grid, the labels and the
 * camera-parented HUD all end up in the normal buffer and every one of them
 * outlines itself.
 */

import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';

/**
 * Layer carrying the surfaces the normal prepass is allowed to see.
 *
 * A layer rather than a visibility sweep because it is one assignment per mesh
 * at build time and one `camera.layers.set` per frame, with nothing to restore
 * per object and nothing to forget to restore. Objects keep layer 0 as well, so
 * this changes nothing about how they are drawn normally.
 */
export const SURFACE_LAYER = 7;

/**
 * Default angle (degrees) between neighbouring normals above which a pixel is
 * called an edge.
 *
 * Deliberately the same 30 as the crease threshold, and for the same reason: it
 * has to sit above the step between adjacent facets of a curved surface or every
 * tube and every flared column head draws its own faceting. A 16-sided duct
 * steps 22 degrees per facet, so 30 clears it while still catching a chamfer.
 */
const DEFAULT_ANGLE = 30;

/** Line colour — the shared outline ink, so intersections read as the same pen
 *  as the contour and the creases rather than as a third effect. */
const DEFAULT_COLOUR = '#241c14';

/**
 * Default line width, as a multiple of the one-device-pixel line the detector
 * draws on its own.
 *
 * 0.5 — a hairline. These lines come on with the Geometry Outline layer,
 * alongside the engine's own edge lines, and they are found per PIXEL rather
 * than built as geometry: every junction in the model draws one, so at full
 * strength they read as a second, heavier pen scribbled over the drawing.
 *
 * BELOW 1 THE WIDTH IS COVERAGE, NOT GEOMETRY. Nothing can draw a third of a
 * pixel, so a sub-pixel width is drawn as a proportionally fainter pixel —
 * which is what a hairline thinner than the raster IS, and how every
 * antialiased renderer draws one. Above 1 the detector's sampling cross is
 * spread instead, which genuinely thickens the line.
 */
const DEFAULT_WIDTH = 0.5;

/**
 * Resolution of the normal buffer, per side, relative to the canvas: 2 gives the
 * edge test four samples per screen pixel — what the canvas's 4x MSAA gives the
 * geometry, which never reaches a line found per pixel. Costs the prepass four
 * times the pixels; the surfaces it draws are normal-only, no lighting.
 */
const SS_SCALE = 2;

/**
 * The intersection-line overlay.
 *
 * `render(renderer, scene, camera)` is called AFTER the main frame has been
 * drawn, and composites over it.
 */
export class NormalEdges {
  private target: THREE.WebGLRenderTarget;
  private readonly normalMaterial: THREE.MeshNormalMaterial;
  private readonly edgeMaterial: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;
  private readonly size = new THREE.Vector2();

  constructor({ angle = DEFAULT_ANGLE, colour = DEFAULT_COLOUR, width = DEFAULT_WIDTH } = {}) {
    // Sized on first render — the viewport's size is not known here and a
    // ResizeObserver already drives the renderer.
    this.target = new THREE.WebGLRenderTarget(1, 1, {
      depthBuffer: true,
      stencilBuffer: false,
      // Nearest, not linear: a filtered normal buffer invents intermediate
      // normals across exactly the discontinuity being looked for, which turns a
      // one-pixel jump into a smooth ramp and softens the line away.
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
    });

    this.normalMaterial = new THREE.MeshNormalMaterial();
    // Both sides, because the exporter draws surfaces DoubleSide and a
    // back-facing triangle left unwritten would read as background and put a
    // false line round the hole.
    this.normalMaterial.side = THREE.DoubleSide;
    // Match the surfaces' own flat shading, so a facet is one constant normal
    // and the threshold below is comparing facet to facet.
    this.normalMaterial.flatShading = true;

    this.edgeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        tNormal: { value: this.target.texture },
        // One SCREEN pixel in uv — the unit the cross and the width are measured in.
        texel: { value: new THREE.Vector2() },
        // One normal-buffer texel in uv — half a screen pixel when the buffer is
        // drawn at SS_SCALE, where the four sub-pixel tests sit.
        subTexel: { value: new THREE.Vector2() },
        // cos of the threshold: normals whose dot falls BELOW this are an edge.
        threshold: { value: Math.cos((angle * Math.PI) / 180) },
        colour: { value: rawColour(colour) },
        // Multiple of the one-pixel line — see DEFAULT_WIDTH.
        width: { value: width },
      },
      // Straight alpha over the finished frame. Depth is neither tested nor
      // written — the quad is not in the scene's depth story at all.
      transparent: true,
      depthTest: false,
      depthWrite: false,
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }`,
      fragmentShader: `
        varying vec2 vUv;
        uniform sampler2D tNormal;
        uniform vec2 texel;
        uniform vec2 subTexel;
        uniform float threshold;
        uniform vec3 colour;
        uniform float width;

        // The edge test at one point of the normal buffer: 0 (no line) .. 1.
        float edgeAt( vec2 uv ) {
          vec4 c = texture2D( tNormal, uv );
          // Alpha 0 = nothing was drawn here. The target is cleared to a
          // transparent black, and MeshNormalMaterial writes alpha 1, so this
          // separates surface from background with no depth read.
          if ( c.a < 0.5 ) return 0.0;
          vec3 n = normalize( c.rgb * 2.0 - 1.0 );

          // Sampling radius: one texel — one device pixel — until the width asks
          // for more. A wider line is a wider cross; a narrower one cannot be, so
          // it is carried in the alpha below instead.
          vec2 r = texel * max( width, 1.0 );

          // Four-neighbour cross. A pixel is an edge when ANY neighbour's normal
          // has swung further than the threshold.
          vec2 offs[4];
          offs[0] = vec2(  r.x, 0.0 );
          offs[1] = vec2( -r.x, 0.0 );
          offs[2] = vec2( 0.0,  r.y );
          offs[3] = vec2( 0.0, -r.y );

          float worst = 1.0;
          for ( int i = 0; i < 4; i++ ) {
            vec4 s = texture2D( tNormal, uv + offs[i] );
            // A neighbour that is background is a SILHOUETTE, not an
            // intersection — SelectionHull already draws those, and counting
            // them here would put a second line on top of the first at double
            // weight. Skipped.
            if ( s.a < 0.5 ) continue;
            worst = min( worst, dot( n, normalize( s.rgb * 2.0 - 1.0 ) ) );
          }

          if ( worst >= threshold ) return 0.0;
          // Ramp over the last of the threshold so the line has a soft shoulder
          // rather than a hard one-pixel stair on a near-tangent junction.
          //
          // WRITTEN AS 1 - smoothstep(lo, hi, x), NOT smoothstep(hi, lo, x).
          // GLSL leaves smoothstep UNDEFINED when edge0 >= edge1, and the
          // reversed form reads so naturally that it is easy to write by
          // accident: some drivers evaluate the polynomial anyway and give the
          // inverted ramp that was intended, others return zero, and a zero here
          // is an overlay that draws nothing at all on one machine and works on
          // the next.
          return 1.0 - smoothstep( threshold - 0.25, threshold, worst );
        }

        void main() {
          // FOUR SAMPLES PER PIXEL, the overlay's own MSAA. The canvas's
          // multisampling never reaches this line — it is found per pixel in an
          // offscreen buffer — so on its own every pixel is either on it or off
          // it and a diagonal junction steps. The buffer is drawn at twice the
          // resolution (see SS_SCALE) and the test is run at the four sub-pixel
          // centres this screen pixel covers; their mean is its coverage. With
          // the buffer at 1x the four taps land on the same texel and this is
          // the single-sample test exactly.
          vec2 h = subTexel * 0.5;
          float a = 0.25 * (
              edgeAt( vUv + vec2( -h.x, -h.y ) ) + edgeAt( vUv + vec2( h.x, -h.y ) )
            + edgeAt( vUv + vec2( -h.x,  h.y ) ) + edgeAt( vUv + vec2( h.x,  h.y ) ) );
          if ( a <= 0.0 ) discard;
          // Sub-pixel width as coverage — see DEFAULT_WIDTH. min(), so a width
          // above 1 does not also brighten the line it has already widened.
          gl_FragColor = vec4( colour, a * min( width, 1.0 ) );
        }`,
    });

    this.quad = new FullScreenQuad(this.edgeMaterial);
  }

  /** Live threshold, in degrees between neighbouring normals. */
  public setAngle(angle: number): void {
    this.edgeMaterial.uniforms.threshold.value = Math.cos((angle * Math.PI) / 180);
  }

  /** Live line width, as a multiple of the one-device-pixel line — see
   *  DEFAULT_WIDTH for what a value below 1 does. */
  public setWidth(width: number): void {
    this.edgeMaterial.uniforms.width.value = Math.max(0.05, width);
  }

  /** Live line colour. */
  public setColour(colour: string): void {
    (this.edgeMaterial.uniforms.colour.value as THREE.Color).copy(rawColour(colour));
  }

  /**
   * Draw the intersection lines over the frame the renderer has just finished.
   *
   * Every piece of renderer state this touches is saved and put back: a viewport
   * that shares its renderer with a thumbnail capture and a shadow refresh
   * cannot afford a pass that leaves the render target, the clear colour or the
   * camera's layer mask somewhere else.
   */
  public render(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.Camera,
  ): void {
    renderer.getDrawingBufferSize(this.size);
    if (this.size.x < 1 || this.size.y < 1) return;
    // Supersampled view (Settings ▸ Line antialiasing): the frame is already at 2x
    // and the line comes out antialiased by the downscale; a further 2x here would
    // be a 4x-per-side buffer for nothing.
    const ss = renderer.getPixelRatio() >= 2 ? 1 : SS_SCALE;
    const tw = this.size.x * ss, th = this.size.y * ss;
    if (this.target.width !== tw || this.target.height !== th) {
      this.target.setSize(tw, th);
    }
    (this.edgeMaterial.uniforms.texel.value as THREE.Vector2)
      .set(1 / this.size.x, 1 / this.size.y);
    (this.edgeMaterial.uniforms.subTexel.value as THREE.Vector2).set(1 / tw, 1 / th);

    const prevTarget = renderer.getRenderTarget();
    const prevAutoClear = renderer.autoClear;
    const prevOverride = scene.overrideMaterial;
    const prevBackground = scene.background;
    const prevMask = camera.layers.mask;
    const prevClear = renderer.getClearColor(_clear).clone();
    const prevClearAlpha = renderer.getClearAlpha();

    // ---- 1. the normal buffer --------------------------------------------
    // Alpha 0 so the shader can tell surface from background without a depth
    // read, and no background object drawn into it for the same reason.
    scene.background = null;
    scene.overrideMaterial = this.normalMaterial;
    camera.layers.set(SURFACE_LAYER);
    renderer.setRenderTarget(this.target);
    renderer.setClearColor(0x000000, 0);
    renderer.autoClear = true;
    renderer.clear();
    renderer.render(scene, camera);

    // ---- 2. the line, over the finished frame ----------------------------
    scene.overrideMaterial = prevOverride;
    scene.background = prevBackground;
    camera.layers.mask = prevMask;
    renderer.setRenderTarget(prevTarget);
    renderer.setClearColor(prevClear, prevClearAlpha);
    // MUST be false: FullScreenQuad.render calls renderer.render, which would
    // otherwise clear the frame this pass exists to draw on top of.
    renderer.autoClear = false;
    this.quad.render(renderer);

    renderer.autoClear = prevAutoClear;
  }

  public dispose(): void {
    this.target.dispose();
    this.normalMaterial.dispose();
    this.edgeMaterial.dispose();
    this.quad.dispose();
  }
}

const _clear = new THREE.Color();

/**
 * A colour whose channels reach the framebuffer as written.
 *
 * `new THREE.Color('#241c14')` decodes to the working (linear) colour space,
 * which is right for a lit material — the renderer encodes back to sRGB on the
 * way out. It is wrong here: three injects that encoding through the
 * `<colorspace_fragment>` chunk of its OWN materials, and a raw ShaderMaterial
 * writes `gl_FragColor` straight to the sRGB drawing buffer with no conversion.
 * A linearised value written raw comes out much darker than the hex asked for —
 * for near-black ink the difference between 0.14 and 0.014.
 */
function rawColour(hex: string): THREE.Color {
  return new THREE.Color().setStyle(hex, THREE.LinearSRGBColorSpace);
}
